import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Linkedin, X, Twitter, MessageCircle, Facebook, Instagram, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import logo from '../pages/images1/logo_L.png'

const LOGO_URL = logo;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/subscribers`, { email });
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.data?.detail === 'Email already subscribed') {
        toast.info('This email is already subscribed.');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-emerald-950 text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex justify-center mb-6">
  <img
    src={LOGO_URL}
    alt="RENVITEK"
    className="h-16"
  />
</div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Engineering the Sustainable Future through research-driven Bio-CNG, solid waste, and wastewater solutions.
            </p>
            <div className="flex gap-4">
              <a href="https://www.linkedin.com/company/renvitek-engineers-llp/" className="w-10 h-10 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-amber-500 transition-colors" data-testid="social-linkedin" target="_blank" rel="noopener noreferrer">
                <Linkedin size={18} />
              </a>
              <a href="https://x.com/renvitek" className="w-10 h-10 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-amber-500 transition-colors" data-testid="social-twitter" target="_blank" rel="noopener noreferrer">
                <X size={18} />
              </a>
              <a href="https://www.facebook.com/share/1DfvZ8JP61/" className="w-10 h-10 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-amber-500 transition-colors" data-testid="social-facebook" target="_blank" rel="noopener noreferrer">
                <Facebook size={18} />
                </a>
                 <a href="https://www.instagram.com/renvitek?igsh=YzljYTk1ODg3Zg=="  data-testid="social-instagram" className="w-10 h-10 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-amber-500 transition-colors" target="_blank" rel="noopener noreferrer">
                <Instagram size={18} />
              </a>
              <a
              href="https://wa.me/918074904718"
              data-testid="social-whatsapp"
              className="w-10 h-10 rounded-full bg-emerald-900 flex items-center justify-center hover:bg-green-500 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle size={18} />
            </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-amber-400">Quick Links</h4>
            <ul className="space-y-3">
  {[
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Core Competencies', path: '/services' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Careers', path: '/careers' },
    { label: 'Contact', path: '/contact' },
  ].map((item) => (
    <li key={item.path}>
      <Link
        to={item.path}
        className="footer-link flex items-center gap-2 text-sm hover:translate-x-1 transition-transform"
      >
        {item.label}
      </Link>
    </li>
  ))}
</ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-amber-400">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin size={20} className="text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-medium">Registered Address:</p>
                  <p className="text-white/70 text-sm">
                    H.No: 3-119/A/BSH/201, Plot No: 119/A, Mayuri Nagar, Miyapur, Hyderabad, TG, India-500049
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <MapPin size={20} className="text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-medium">Office Address:</p>
                  <p className="text-white/70 text-sm">
                    DEIS-JNTUH, 1st Floor, New Admission Building, JNTUH, Kukatpally, Hyderabad – 85
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={20} className="text-amber-400" />
                <div>
                   <a href="tel:+918074904918" className="text-white/70 text-sm hover:text-amber-400">+91-8074904918 (Office)</a>
                  <span className="text-white/50 mx-2">|</span>
                  <a href="tel:04045302865" className="text-white/70 text-sm hover:text-amber-400">040-45302865</a>
                  <span className="text-white/50 mx-2">|</span>
                  <a href="tel:+919885884824" className="text-white/70 text-sm hover:text-amber-400">+91-9885884824</a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={20} className="text-amber-400" />
                <a href="mailto:info@renvitek.in" className="text-white/70 text-sm hover:text-amber-400">info@renvitek.in</a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-amber-400">Newsletter</h4>
            <p className="text-white/70 text-sm mb-4">
              Subscribe to receive updates on our latest innovations and solutions.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full bg-emerald-900 border border-emerald-800 rounded-none px-4 py-3 text-white placeholder:text-white/50 focus:outline-none focus:border-amber-400 transition-colors"
                  data-testid="subscribe-email-input"
                />
              </div>
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-400 text-emerald-950 py-3 font-semibold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="subscribe-btn"
              >
                <Send size={18} />
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </motion.button>
            </form>
          </div>
        </div>

        {/* Google Map */}
        <div className="mt-12 pt-12 border-t border-emerald-900">
          <h4 className="text-lg font-semibold mb-6 text-amber-400">Our Location</h4>
          <div className="map-container rounded-none overflow-hidden">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3805.30217774561!2d78.39079227516736!3d17.49308288341222!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTfCsDI5JzM1LjEiTiA3OMKwMjMnMzYuMSJF!5e0!3m2!1sen!2sin!4v1771683661142!5m2!1sen!2sin" 
              width="100%" 
              height="300" 
              style={{ border: 0 }}
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="RENVITEK Location"
              data-testid="google-map"
            />
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-emerald-900/50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} RENVITEK Engineers LLP. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-white/60 text-sm hover:text-amber-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/60 text-sm hover:text-amber-400 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
